/**
 * Normalizes MongoDB extended JSON ($oid, etc.) from upstream API responses
 * into plain values suitable for API requests and UI state.
 */
export function normalizeOid<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((item) => normalizeOid(item)) as T;
    }

    if ('$oid' in value && typeof (value as { $oid: unknown }).$oid === 'string') {
      return (value as { $oid: string }).$oid as T;
    }

    const normalized: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      normalized[key] = normalizeOid(val);
    }
    return normalized as T;
  }

  return value;
}

/** Extracts a string id from a plain string or MongoDB `{ $oid: string }` value. */
export function extractOid(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  if (value && typeof value === 'object' && '$oid' in value) {
    const oid = (value as { $oid: unknown }).$oid;
    return typeof oid === 'string' && oid.length > 0 ? oid : undefined;
  }

  return undefined;
}
